import { Box, Menu, MenuItem } from '@mui/material';
import classnames from 'classnames';
import {
  applyRecipe,
  deselectAllRecipeBuilderAttrs,
  recipeApplied,
  setExpandedRecipe,
  setExpandedRecipeGroup,
  setFormError,
  setRecipeGroups,
} from 'js/actions';
import { GenAITransformProvider } from 'js/components/recipeBuilder/GenAiTransformButton/GenAITransformContext';
import { GET_TRANSFORMED_SAMPLES_REQUEST } from 'js/constants/actionTypes';
import { registerTransformDSLCompletion } from 'js/constants/monaco-transform-dsl';
import { FLOW_TYPE_SPARK, FLOW_TYPE_STREAMING } from 'js/constants/pipelineConstants';
import {
  FilterTypesKeys,
  RECIPE_GROUP_ANNOTATIONS,
  RECIPE_GROUP_DEFAULTS,
  RECIPE_GROUP_DESCRIPTIONS,
  RECIPE_GROUP_FILTERS,
  RECIPE_GROUP_LABELS,
  RECIPE_GROUP_ORDER,
  RECIPE_GROUP_TRANSFORMS,
  RECIPE_GROUP_TRANSFORMS_AGGREGATE,
  RECIPE_GROUP_TRANSFORMS_CODE,
  RECIPE_GROUP_TRANSFORMS_REPLICATE,
  RECIPE_GROUP_VALIDATIONS,
  SchemaBrowserType,
  SchemaEditorViewModeType,
  TransformParameterTypes,
  TX_BUILDER_RECIPE_GROUPS,
} from 'js/constants/schemaEditor';
import { GROUPED_TRANSFORM_RULES } from 'js/constants/transforms';
import { getSuggestions } from 'js/lib/RecipeBuilderUtils';
import { find, findLastIndex, isArray, isEmpty, isNil, isPlainObject, keys, map } from 'lodash';
import { DraggableElement } from 'new/components/DraggableElement';
import flowTypes from 'new/components/flows/types/flowTypes';
import { ReactComponent as PlusIcon } from 'new/components/icons/fi-rr-plus.svg';
import { Button } from 'new/elements/Button';
import React from 'react';
import { connect } from 'react-redux';
import Constants from '../../Constants';
import JSONUtils from '../../lib/JSONUtils';
import { getFeatureFlagValue } from 'new/constants/featureFlags';
import { isCapsuleCluster } from 'js/lib/PipelineUtils';
import ConfigureRecipeHeader from './ConfigureRecipeHeader';
import ConfigureRecipeTypeMenu from './ConfigureRecipeTypeMenu';
import { GenAiTransformButton } from './GenAiTransformButton';
import RecipeBuilderSuggestions from './RecipeBuilderSuggestions';
import RecipeGroupsList from './RecipeGroupsList';
import RecipeIcon from './icons/RecipeIcon';
import RecipeTypeAnnotationsIcon from './icons/RecipeTypeAnnotationsIcon';
import RecipeTypeDefaultsIcon from './icons/RecipeTypeDefaultsIcon';
import RecipeTypeFiltersIcon from './icons/RecipeTypeFiltersIcon';
import RecipeTypeTransformsAggregateIcon from './icons/RecipeTypeTransformsAggregateIcon';
import RecipeTypeTransformsCodeIcon from './icons/RecipeTypeTransformsCodeIcon';
import RecipeTypeTransformsIcon from './icons/RecipeTypeTransformsIcon';
import RecipeTypeTransformsReplicateIcon from './icons/RecipeTypeTransformsReplicateIcon';
import RecipeTypeValidationsIcon from './icons/RecipeTypeValidationsIcon';

const AGGREGATION_ENABLED_FLOW_TYPES = [FLOW_TYPE_STREAMING, FLOW_TYPE_SPARK];

class ConfigureRecipe extends React.PureComponent<any> {
  state = {
    areSuggestionsExpanded: false,
    recipeTypeMenuAnchorEl: null,
  };

  componentDidMount() {
    const { isEditMode } = this.props;

    if (!isEditMode) {
      this.setState({ areSuggestionsExpanded: true });
    }

    this._registerDSLCompletion();

    // global keyboard listener for running Nexset rules
    window.addEventListener('keydown', this._handleKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this._handleKeyDown);
  }

  componentDidUpdate(prevProps) {
    const { autoApply, selectedAttrs } = this.props;

    if (autoApply && !prevProps.autoApply) {
      this._handleApplyRecipeClicked();
    }

    if (
      selectedAttrs !== prevProps.selectedAttrs &&
      (!isEmpty(selectedAttrs[SchemaBrowserType.INPUT]) ||
        !isEmpty(selectedAttrs[SchemaBrowserType.OUTPUT]) ||
        !isEmpty(selectedAttrs[SchemaBrowserType.SCHEMA_SPEC]))
    ) {
      this.setState({ areSuggestionsExpanded: true });
    }

    if (
      this.props.inputSchema !== prevProps.inputSchema ||
      this.props.outputSchema !== prevProps.outputSchema ||
      this.props.metaDataSpec !== prevProps.metaDataSpec ||
      this.props.transforms !== prevProps.transforms ||
      this.props.dataMaps !== prevProps.dataMaps
    ) {
      this._registerDSLCompletion();
    }
  }

  render() {
    const {
      dataSet,
      recipeGroups,
      isRecipeUnapplied,
      transforms,
      reusableTransformIds,
      publicReusableTransformIds,
      selectedAttrs,
      inputSchema,
      inputSchemaNested,
      inputMetaSchemaNested,
      metaDataSpec,
      outputSchema,
      outputSchemaNested,
      schemaSpecSchema,
      isLoadingInitialData,
      isLoadingTransformedSamples,
      loadReusableTransforms,
    } = this.props;
    const { areSuggestionsExpanded, recipeTypeMenuAnchorEl } = this.state;
    const isRecipeEmpty = !isLoadingInitialData && isEmpty(recipeGroups);

    return (
      <div className="configure-recipe">
        <ConfigureRecipeHeader
          showAsyncControl={dataSet?.flow_type !== flowTypes.api_server}
          isRecipeUnapplied={isRecipeUnapplied}
          isLoading={isLoadingTransformedSamples}
          onApplyRecipeClicked={this._handleApplyRecipeClicked}
        />
        <GenAITransformProvider runNexsetRules={this._handleApplyRecipeClicked}>
          <div
            className={classnames('configure-recipe-body', {
              'is-empty': isRecipeEmpty,
            })}
          >
            <RecipeGroupsList
              dataSet={dataSet}
              recipeGroups={recipeGroups}
              outputSchema={outputSchema}
              isMultiBlockEnabled={this.props.isMultiBlockEnabled}
              onLoadReusableTransforms={loadReusableTransforms}
              onApplyRecipe={this._handleApplyRecipeClicked}
            />
            {!isLoadingInitialData && (
              <>
                {isRecipeEmpty && (
                  <>
                    <RecipeIcon width={68} height={71} />
                    <h1>Nothing here</h1>
                    <p>
                      To start setting Nexset Rules for your data, you can use the recommendations
                      below or use the "Add Rules Group" button to start.
                    </p>
                  </>
                )}
                <Box gap={2} display="flex" alignItems="center">
                  <Button
                    color="primary"
                    variant="outlined"
                    className="add-recipe-group-button"
                    onClick={this._handleAddRecipeGroupClicked}
                  >
                    <PlusIcon width={12} height={12} className="add-recipe-group-button-icon" />
                    Add Rule Group
                  </Button>

                  <DraggableElement>
                    <GenAiTransformButton
                      id="nexset-rules"
                      buttonText="Generate"
                      nexsetId={dataSet?.id}
                    />
                  </DraggableElement>
                </Box>
                <ConfigureRecipeTypeMenu
                  open={!!recipeTypeMenuAnchorEl}
                  anchorEl={recipeTypeMenuAnchorEl}
                  onClose={this._handleRecipeTypeMenuClosed}
                  onRecipeTypeClicked={this._handleRecipeTypeClicked}
                  isRecipeGroupOptionDisabled={this._isRecipeGroupOptionDisabled}
                  getRecipeTypeIcon={this._getRecipeTypeIcon}
                />
              </>
            )}
          </div>
          <RecipeBuilderSuggestions
            recommendationItemOnTop={
              <DraggableElement>
                <GenAiTransformButton
                  id="recommendations"
                  as="recommendation"
                  buttonText="Generate with NOVA"
                  nexsetId={dataSet?.id}
                />
              </DraggableElement>
            }
            suggestions={getSuggestions(
              transforms,
              recipeGroups,
              selectedAttrs,
              inputSchema,
              inputSchemaNested,
              inputMetaSchemaNested,
              metaDataSpec,
              outputSchema,
              outputSchemaNested,
              schemaSpecSchema,
              reusableTransformIds,
              publicReusableTransformIds,
            )}
            isExpanded={areSuggestionsExpanded}
            onToggleExpand={this._handleToggleExpandSuggestions}
            onSuggestionClicked={this._handleSuggestionClicked}
          />
        </GenAITransformProvider>
      </div>
    );
  }

  _getRecipeTypeIcon = (type) => {
    switch (type) {
      case RECIPE_GROUP_TRANSFORMS:
        return <RecipeTypeTransformsIcon />;

      case RECIPE_GROUP_TRANSFORMS_CODE:
        return <RecipeTypeTransformsCodeIcon />;

      case RECIPE_GROUP_TRANSFORMS_AGGREGATE:
        return <RecipeTypeTransformsAggregateIcon />;

      case RECIPE_GROUP_TRANSFORMS_REPLICATE:
        return <RecipeTypeTransformsReplicateIcon />;

      case RECIPE_GROUP_DEFAULTS:
        return <RecipeTypeDefaultsIcon />;

      case RECIPE_GROUP_FILTERS:
        return <RecipeTypeFiltersIcon />;

      case RECIPE_GROUP_VALIDATIONS:
        return <RecipeTypeValidationsIcon />;

      case RECIPE_GROUP_ANNOTATIONS:
        return <RecipeTypeAnnotationsIcon />;

      default:
        return null;
    }
  };

  /**
   * TEMPORARY: Original guardrail logic from develop branch
   *
   * This is the exact implementation before PR #2889 guardrails (GUARDRAIL 1, 2, 3).
   * Used when multi-block feature flag is OFF.
   *
   * REMOVE THIS METHOD after full rollout validation (few weeks)
   */
  _isRecipeGroupOptionDisabled_Original = (type) => {
    const { dataSet, recipeGroups } = this.props;

    if (type === RECIPE_GROUP_TRANSFORMS_AGGREGATE && isCapsuleCluster(dataSet)) {
      return {
        disabled: true,
        message: 'SQL transforms are not supported for capsule cluster datasets.',
      };
    }

    if (
      type === RECIPE_GROUP_TRANSFORMS_AGGREGATE &&
      !AGGREGATION_ENABLED_FLOW_TYPES.includes(dataSet?.flow_type)
    ) {
      return {
        disabled: true,
        message: 'This rule group is not available for this type of flow.',
      };
    }

    const incompatibilityReturnValue = {
      disabled: true,
      message: 'This rule group is incompatible with the added rules.',
    };

    // disable replicate if a replicate group already exists
    if (
      type === RECIPE_GROUP_TRANSFORMS_REPLICATE &&
      find(recipeGroups, { type: RECIPE_GROUP_TRANSFORMS_REPLICATE })
    ) {
      return incompatibilityReturnValue;
    }

    // disable tx builder and related groups IF aggregate or code tx exists
    if (
      (type === RECIPE_GROUP_TRANSFORMS_REPLICATE || TX_BUILDER_RECIPE_GROUPS.includes(type)) &&
      find(recipeGroups, (group) =>
        [RECIPE_GROUP_TRANSFORMS_CODE, RECIPE_GROUP_TRANSFORMS_AGGREGATE].includes(group.type),
      )
    ) {
      return incompatibilityReturnValue;
    }

    // disable code tx IF any tx exists
    if (
      [RECIPE_GROUP_TRANSFORMS_CODE, RECIPE_GROUP_TRANSFORMS_AGGREGATE].includes(type) &&
      find(
        recipeGroups,
        (group) =>
          [
            RECIPE_GROUP_TRANSFORMS_CODE,
            RECIPE_GROUP_TRANSFORMS_AGGREGATE,
            RECIPE_GROUP_TRANSFORMS_REPLICATE,
          ].includes(group.type) || TX_BUILDER_RECIPE_GROUPS.includes(group.type),
      )
    ) {
      return incompatibilityReturnValue;
    }

    return { disabled: false };
  };

  _isRecipeGroupOptionDisabled = (type) => {
    // ═══════════════════════════════════════════════════════════════════════════
    // EARLY EXIT: Feature Flag Disabled - Use Original Guardrails
    // ═══════════════════════════════════════════════════════════════════════════
    // TEMPORARY: Remove this check after full rollout validation (few weeks)
    // ═══════════════════════════════════════════════════════════════════════════
    if (!this.props.isMultiBlockEnabled) {
      return this._isRecipeGroupOptionDisabled_Original(type);
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // NEW GUARDRAILS (PR #2889 + Multi-Block Support)
    // ═══════════════════════════════════════════════════════════════════════════
    // Code below only executes when multi-block feature flag is ON
    // ═══════════════════════════════════════════════════════════════════════════

    const { dataSet, recipeGroups } = this.props;

    if (type === RECIPE_GROUP_TRANSFORMS_AGGREGATE && isCapsuleCluster(dataSet)) {
      return {
        disabled: true,
        message: 'SQL transforms are not supported for capsule cluster datasets.',
      };
    }

    if (
      type === RECIPE_GROUP_TRANSFORMS_AGGREGATE &&
      !AGGREGATION_ENABLED_FLOW_TYPES.includes(dataSet?.flow_type)
    ) {
      return {
        disabled: true,
        message: 'This rule group is not available for this type of flow.',
      };
    }

    const incompatibilityReturnValue = {
      disabled: true,
      message: 'This rule group is incompatible with the added rules.',
    };

    // disable replicate if a replicate group already exists
    if (
      type === RECIPE_GROUP_TRANSFORMS_REPLICATE &&
      find(recipeGroups, { type: RECIPE_GROUP_TRANSFORMS_REPLICATE })
    ) {
      return incompatibilityReturnValue;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // GUARDRAIL 1: Reusable Transform Restrictions
    // ═══════════════════════════════════════════════════════════════════════════
    // Reusable Transform (within a Tx Code block) can ONLY exist if it's the sole Tx block
    // - Cannot add other Tx blocks if a Reusable Transform exists
    // Note: The dropdown restriction (preventing selection of Reusable Transform option)
    // is handled in TransformCodeRecipeGroup.js

    const hasReusableTxCode = recipeGroups.some(
      (group) => group.type === RECIPE_GROUP_TRANSFORMS_CODE && group.data?.type === 'reusable_tx',
    );

    // If Reusable Transform exists, prevent adding any other Tx blocks
    if (
      hasReusableTxCode &&
      [
        RECIPE_GROUP_TRANSFORMS,
        RECIPE_GROUP_TRANSFORMS_CODE,
        RECIPE_GROUP_TRANSFORMS_AGGREGATE,
        RECIPE_GROUP_TRANSFORMS_REPLICATE,
        RECIPE_GROUP_DEFAULTS,
        RECIPE_GROUP_FILTERS,
      ].includes(type)
    ) {
      return {
        disabled: true,
        message:
          'Cannot add this transform block when using a Reusable Transform. Remove the Reusable Transform first.',
      };
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // GUARDRAIL 2: Tx Aggregate Restrictions
    // ═══════════════════════════════════════════════════════════════════════════
    // Tx Aggregate cannot be mixed with other Tx blocks

    const hasAggregate = recipeGroups.some(
      (group) => group.type === RECIPE_GROUP_TRANSFORMS_AGGREGATE,
    );

    const hasOtherTxBlocks = recipeGroups.some((group) =>
      [RECIPE_GROUP_TRANSFORMS, RECIPE_GROUP_TRANSFORMS_CODE].includes(group.type),
    );

    // If trying to add Aggregate and other Tx blocks exist
    if (type === RECIPE_GROUP_TRANSFORMS_AGGREGATE && hasOtherTxBlocks) {
      return {
        disabled: true,
        message:
          'Aggregation cannot be mixed with other transform blocks. Remove other transform blocks first.',
      };
    }

    // If Aggregate exists and trying to add other Tx blocks
    if (hasAggregate && [RECIPE_GROUP_TRANSFORMS, RECIPE_GROUP_TRANSFORMS_CODE].includes(type)) {
      return {
        disabled: true,
        message:
          'Cannot add transform blocks when using Aggregation. Remove the Aggregation block first.',
      };
    }

    return { disabled: false };
  };

  _registerDSLCompletion = () => {
    const { inputSchema, outputSchema, metaDataSpec, transforms, dataMaps, dataMapSchemas } =
      this.props;

    registerTransformDSLCompletion(
      Object.values(GROUPED_TRANSFORM_RULES.rules),
      inputSchema,
      outputSchema,
      metaDataSpec,
      transforms,
      dataMaps,
      dataMapSchemas,
    );
  };

  _handleAddRecipeGroupClicked = (e) => {
    this.setState({ recipeTypeMenuAnchorEl: e.currentTarget });
  };

  _handleRecipeTypeMenuClosed = () => {
    this.setState({ recipeTypeMenuAnchorEl: null });
  };

  /**
   * _handleRecipeTypeClicked - Handles adding a new recipe group to the configuration
   *
   * Recipe groups are the building blocks of data transformation in Nexla.
   * Each group type corresponds to different JOLT operations when processed
   * by getTxFromRecipe() in TransformsUtils.js.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * RECIPE GROUP TYPES AND THEIR JOLT OUTPUT:
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * RECIPE_GROUP_TRANSFORMS (Transform Builder):
   *   - UI: Visual attribute transformations using DSL
   *   - JOLT: nexla.modify operation with transformation spec
   *   - Example: { operation: "nexla.modify", spec: { "attr": "=toUpper(...)" } }
   *
   * RECIPE_GROUP_TRANSFORMS_CODE (Custom Code):
   *   - UI: Python/JavaScript code editor
   *   - JOLT: nexla.custom operation with base64-encoded script
   *   - Example: { operation: "nexla.custom", spec: { language: "python", script: "..." } }
   *
   * RECIPE_GROUP_TRANSFORMS_AGGREGATE (SQL Aggregation):
   *   - UI: SQL editor for aggregation queries
   *   - JOLT: nexla.custom with SQL-specific options
   *   - Example: { operation: "nexla.custom", spec: { language: "sql", code_type: "SPARK_SQL" } }
   *
   * RECIPE_GROUP_FILTERS (Record Filters):
   *   - UI: Condition builder to filter records
   *   - JOLT: nexla.filter operation
   *   - Example: { operation: "nexla.filter", spec: { filter: "base64-encoded-jsonpath" } }
   *
   * RECIPE_GROUP_DEFAULTS (Default Values):
   *   - UI: Set default values for attributes
   *   - JOLT: default operation (merged with scaffolding in legacy mode)
   *   - Example: { operation: "default", spec: { "attr": "defaultValue" } }
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * BLOCK BEHAVIOR:
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * The ORDER of recipe groups affects JOLT generation:
   *
   * LEGACY MODE (single TRANSFORMS + optional FILTERS/DEFAULTS):
   *   - All groups combine into ONE block
   *   - No nexla.end_block inserted
   *   - Full scaffolding wraps everything
   *
   * MULTI-BLOCK MODE (multiple TRANSFORMS or any CODE):
   *   - Each group becomes its OWN block
   *   - nexla.end_block inserted between blocks
   *   - Scaffolding only on first builder block (if builder-first)
   *
   * See docs/block-combinations-test-scenarios.md for detailed examples.
   */
  _handleRecipeTypeClicked = (type) => {
    const {
      dataSet,
      inputSchema,
      recipeGroups,
      selectedAttrs,
      isLegacyMode,
      setRecipeGroups,
      setExpandedRecipeGroup,
      setExpandedRecipe,
    } = this.props;
    const getSelectedAttr = (gridId) => keys(selectedAttrs[gridId])[0] || '';
    let data;

    switch (type) {
      case RECIPE_GROUP_TRANSFORMS:
        const selectedAttr = getSelectedAttr(SchemaBrowserType.INPUT);
        data = [
          isLegacyMode
            ? {
                __id: crypto.randomUUID(),
                type: TransformParameterTypes.FUNCTION,
                name: 'eqAttr',
                parameters: {
                  __srcAttr: {
                    type: TransformParameterTypes.ATTR_REF,
                    name: selectedAttr,
                  },
                },
                __destAttr: selectedAttr,
              }
            : {
                __id: crypto.randomUUID(),
                code: '',
              },
        ];
        break;

      case RECIPE_GROUP_TRANSFORMS_CODE:
        const codeLanguage = Constants.CodeLanguages.PYTHON;
        const defaultCode = Constants.DefaultCode[Constants.CodeLanguages.PYTHON];
        data = {
          type: 'custom_code',
          codeLanguage,
          code: defaultCode,
        };
        break;

      case RECIPE_GROUP_TRANSFORMS_AGGREGATE:
        const attr =
          (find(inputSchema, { __type: 'string' }) || inputSchema?.[0])?.__name || 'attribute';

        data = {
          type: 'custom_code',
          codeLanguage:
            dataSet?.flow_type === FLOW_TYPE_SPARK
              ? Constants.CodeLanguages.SPARK_SQL
              : Constants.CodeLanguages.FLINK_SQL,
          code: `SELECT \`${attr}\`, COUNT(\`${attr}\`) \`${attr}_count\`
FROM {nexset_this}
GROUP BY \`${attr}\``,
          options: {
            aggregation_window: 'default',
          },
        };
        break;

      case RECIPE_GROUP_TRANSFORMS_REPLICATE:
        data = true;
        break;

      case RECIPE_GROUP_FILTERS:
        data = [
          {
            type: TransformParameterTypes.FILTER_GROUP,
            parameters: [
              {
                type: TransformParameterTypes.FILTER_RULE,
                ruleOp: FilterTypesKeys.FILTER_NOT_NULL,
                ruleVal: {
                  type: 'nullValue',
                  value: null,
                },
                ruleAttribute: getSelectedAttr(SchemaBrowserType.OUTPUT),
              },
            ],
          },
        ];
        break;

      case RECIPE_GROUP_DEFAULTS:
        data = [
          {
            type: 'defaults',
            parameters: {
              defaultType: 'DEFAULT_NONE',
              value: null,
            },
            __destAttr: getSelectedAttr(SchemaBrowserType.OUTPUT),
          },
        ];
        break;

      case RECIPE_GROUP_ANNOTATIONS:
        data = [
          {
            attr: getSelectedAttr(SchemaBrowserType.OUTPUT),
            description: '',
          },
        ];
        break;

      case RECIPE_GROUP_VALIDATIONS:
        data = [];
        break;

      default:
    }

    setRecipeGroups([...recipeGroups, { type, data }], true);
    setExpandedRecipeGroup(recipeGroups.length);

    if (isArray(data) && data.length === 1) {
      setExpandedRecipe(0);
    }

    this.setState({
      areSuggestionsExpanded: false,
      recipeTypeMenuAnchorEl: null,
    });
  };

  _handleToggleExpandSuggestions = () => {
    const { areSuggestionsExpanded } = this.state;

    this.setState({ areSuggestionsExpanded: !areSuggestionsExpanded });
  };

  _handleSuggestionClicked = (recipeGroup) => {
    const { recipeGroups, setExpandedRecipeGroup, setRecipeGroups } = this.props;

    // merge recipeGroup data with existing recipeGroups of the same type
    const newRecipeGroups = [...recipeGroups];
    const existingRecipeGroupIndex = findLastIndex(newRecipeGroups, {
      type: recipeGroup.type,
    });
    let affectedRecipeGroupIndex;

    if (
      existingRecipeGroupIndex > -1 &&
      [
        RECIPE_GROUP_TRANSFORMS,
        RECIPE_GROUP_DEFAULTS,
        RECIPE_GROUP_VALIDATIONS,
        RECIPE_GROUP_ANNOTATIONS,
      ].includes(recipeGroup.type)
    ) {
      const existingRecipeGroup = newRecipeGroups[existingRecipeGroupIndex];
      newRecipeGroups[existingRecipeGroupIndex] = {
        ...existingRecipeGroup,
        data: [...existingRecipeGroup.data, ...recipeGroup.data],
      };
      affectedRecipeGroupIndex = existingRecipeGroupIndex;
    } else {
      newRecipeGroups.push(recipeGroup);
      affectedRecipeGroupIndex = newRecipeGroups.length - 1;
    }

    setRecipeGroups(newRecipeGroups, false, true);
    setExpandedRecipeGroup(affectedRecipeGroupIndex);
    this.setState({ areSuggestionsExpanded: false });
  };

  _handleApplyRecipeClicked = () => {
    const {
      formType,
      recipeGroups,
      inputSamples,
      customSamplesCode,
      selectedViewMode,
      showMeta,
      recipeApplied,
      deselectAllRecipeBuilderAttrs,
      setFormError,
      applyRecipe,
    } = this.props;

    // parse user-entered input samples
    let samples;

    if (selectedViewMode === SchemaEditorViewModeType.CODE && !isNil(customSamplesCode)) {
      let customSamples = JSONUtils.parseJson(customSamplesCode);

      if (isPlainObject(customSamples)) {
        customSamples = [customSamples];
      } else if (!isArray(customSamples)) {
        setFormError(formType, 'Please enter a valid JSON array.');
        return;
      }

      samples = showMeta
        ? customSamples
        : map(customSamples, (sample, index) => ({
            rawMessage: sample,
            nexlaMetaData:
              inputSamples?.[index]?.nexlaMetaData || inputSamples?.[0]?.nexlaMetaData || {},
          }));
    } else {
      samples = inputSamples;
    }

    deselectAllRecipeBuilderAttrs();
    applyRecipe(recipeGroups, samples);
  };

  _handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      this._handleApplyRecipeClicked();
    }
  };
}

const mapStateToProps = (state) => {
  const {
    entities: { transforms, dataMaps },
    forms: { activeType: formType },
    recipeBuilder: {
      recipeGroups,
      isRecipeUnapplied,
      isLegacyMode,
      autoApply,
      selectedAttrs,
      appliedSchemaSpecId,
    },
    schemaEditor: {
      fromSchema,
      toSchema,
      fromSamples: { fromNested },
      filterDisplayOptions,
      defaultsDisplayOptions,
      metaDataSpec,
    },
    dataMaps: { schemas: dataMapSchemas },
    transforms: {
      reusableRecordTransforms: {
        sortedIds: reusableTransformIds,
        sortedPublicIds: publicReusableTransformIds,
      },
    },
    schemaSpecs: { nestedSchemas },
    request,
  } = state;

  const schemaSpecSchema = appliedSchemaSpecId && nestedSchemas[appliedSchemaSpecId];
  const isLoadingTransformedSamples = !!request[GET_TRANSFORMED_SAMPLES_REQUEST];

  // TEMPORARY: Get multi-block feature flag (remove after rollout)
  const isMultiBlockEnabled = getFeatureFlagValue('UI_ENABLE_MULTI_BLOCK_TRANSFORMS');

  return {
    transforms,
    dataMaps,
    formType,
    recipeGroups,
    isRecipeUnapplied,
    isLegacyMode,
    autoApply,
    selectedAttrs,
    inputSchema: fromSchema.flat,
    inputSchemaNested: fromSchema.byAttrId,
    inputMetaSchemaNested: fromSchema.metaByAttrId,
    metaDataSpec,
    outputSchema: toSchema.flat,
    outputSchemaNested: toSchema.byAttrId,
    inputSamples: fromNested,
    schemaSpecSchema,
    dataMapSchemas,
    filterDisplayOptions,
    defaultsDisplayOptions,
    reusableTransformIds,
    publicReusableTransformIds,
    isLoadingTransformedSamples,
    isMultiBlockEnabled,
  };
};

export default connect(mapStateToProps, {
  setRecipeGroups,
  setExpandedRecipeGroup,
  setExpandedRecipe,
  recipeApplied,
  applyRecipe,
  setFormError,
  deselectAllRecipeBuilderAttrs,
})(ConfigureRecipe);
